import ButtonOutline from 'components/common/button/outline';
import ButtonPrimary from 'components/common/button/primary';
import CustomSelectPrimary from 'components/common/custom-select/primary';
import HideOn from 'components/common/hide-on';
import { useModal } from 'hooks/use-modal';
import { ADVERT_TYPE, PROPERTY_TYPE } from 'types';
import { FilterXIcon } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useGetApiV1ClientCitiesQuery, useGetApiV1ClientCountriesQuery } from 'service/api/allApis';
import { RenderIf } from 'components/common/render-if';
import { useLanguage } from 'hooks/use-language';

type FormValues = {
    advertType: string;
    advertOwnerType: string;
    propertyType: string;
    countryId: string;
    cityId: string;
    districtId: string;
    price: string;
    roomCount: string;
    buildingFloorCount: string;
    propertyFloor: string;
    propertyArea: string;
    propertyLandArea: string;
    maintenanceStatus: string;
    buildingType: string;
    documentStatus: string;
    mortgageStatus: string;
    priceType: string;
    description: string;
    name: string;
    surname: string;
    email: string;
    phoneNumber: string;
    password: string;
    confirmedPassword: string;
};
const ListingFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedLanguageId } = useLanguage();
    const { onOpen } = useModal();
    const { t } = useTranslation();

    const advertType = searchParams.get('advertType') || '';
    const advertOwnerType = searchParams.get('advertOwnerType') || '';
    const propertyType = searchParams.get('propertyType') || '';
    const countryId = searchParams.get('countryId') || '';
    const cityId = searchParams.get('cityId') || '';
    const roomCount = searchParams.get('roomCount') || '';

    const { watch, setValue, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            advertType: '',
            advertOwnerType: '',
            propertyType: '',
            countryId: '',
            cityId: '',
            roomCount: '',
        },
    });
    const countryIdValue = watch('countryId');
    const hasAnyFilter = searchParams.toString().length > 0;

    const { data: countryData, isLoading: isCountriesLoading } = useGetApiV1ClientCountriesQuery({
        isPaginated: false,
    });
    const { data: cityData, isLoading: isCitiesLoading } = useGetApiV1ClientCitiesQuery(
        { isPaginated: false, countryId: countryIdValue },
        { skip: !countryIdValue },
    );

    const countryOptions = useMemo(() => {
        if (!countryData?.items) return [];
        return countryData.items.map((item) => {
            const label =
                item.countryDictionaryGetAllDtos?.find(
                    (item) => item.languageId === selectedLanguageId,
                )?.name || item.name!;
            return { value: item.id!, label };
        });
    }, [countryData, selectedLanguageId]);

    const cityOptions = useMemo(() => {
        if (!cityData?.items) return [];
        return cityData.items.map((item) => {
            const label =
                item.cityDictionaryGetAllDtos?.find(
                    (item) => item.languageId === selectedLanguageId,
                )?.name || item.name!;
            return { value: item.id!, label };
        });
    }, [cityData, selectedLanguageId]);

    const advertTypeOptions = [
        {
            value: '',
            label: t('general.all'),
        },
        {
            value: String(ADVERT_TYPE.RENT),
            label: t('advert.rent'),
        },
        {
            value: String(ADVERT_TYPE.SALE),
            label: t('advert.sale'),
        },
    ];
    const propertyTypeOptions = [
        {
            value: '',
            label: t('general.all'),
        },
        {
            value: String(PROPERTY_TYPE.NEW_BUILDING),
            label: t('advert.new-building'),
        },
        {
            value: String(PROPERTY_TYPE.OLD_BUILDING),
            label: t('advert.old-building'),
        },
        {
            value: String(PROPERTY_TYPE.COMMERCIAL),
            label: t('advert.commercial'),
        },
        {
            value: String(PROPERTY_TYPE.OFFICE),
            label: t('advert.office'),
        },
        {
            value: String(PROPERTY_TYPE.GARAGE),
            label: t('advert.garage'),
        },
        {
            value: String(PROPERTY_TYPE.YARD_HOUSE),
            label: t('advert.yard-house'),
        },
        {
            value: String(PROPERTY_TYPE.LAND),
            label: t('advert.land'),
        },
    ];
    const roomCountOptions = [
        {
            value: '',
            label: t('general.all'),
        },
        {
            value: '1',
            label: t('advert.room-with-count', { count: 1 }),
        },
        {
            value: '2',
            label: t('advert.room-with-count', { count: 2 }),
        },
        {
            value: '3',
            label: t('advert.room-with-count', { count: 3 }),
        },
        {
            value: '4',
            label: t('advert.room-with-count', { count: 4 }),
        },
        {
            value: '5',
            label: t('advert.room-with-count', { count: 5 }),
        },
        {
            value: '6',
            label: t('advert.room-with-count', { count: 6 }),
        },
    ];

    const onSubmit: SubmitHandler<FormValues> = (values) => {
        Object.entries(values).forEach(([key, value]) => {
            if (value) searchParams.set(key, value);
            else searchParams.delete(key);
        });
        setSearchParams(searchParams);
    };

    const onReset = () => {
        setSearchParams('');
        reset();
    };

    useEffect(() => {
        setValue('advertType', advertType);
    }, [advertType]);

    useEffect(() => {
        setValue('advertOwnerType', advertOwnerType);
    }, [advertOwnerType]);

    useEffect(() => {
        setValue('propertyType', propertyType);
    }, [propertyType]);

    useEffect(() => {
        setValue('countryId', countryId);
    }, [countryId]);

    useEffect(() => {
        setValue('cityId', cityId);
    }, [cityId]);

    useEffect(() => {
        setValue('roomCount', roomCount);
    }, [roomCount]);

    return (
        <div className="md:bg-brend-20 -mt-8 md:-mt-12 py-4 z-10 relative">
            <form
                onSubmit={handleSubmit(onSubmit)}
                autoComplete="on"
                className="w-full flex flex-col gap-y-4"
            >
                <div className="w-full  md:w-[925px] lg:w-[1240px] mx-auto px-3 md:px-0 grid grid-cols-2 md:grid-cols-[repeat(4,1fr)] lg:grid-cols-[repeat(5,1fr)_120px_120px_40px] gap-x-2 gap-y-2 md:gap-y-4">
                    <CustomSelectPrimary
                        options={advertTypeOptions}
                        placeholder={t('advert.purpose')}
                        value={watch('advertType') || undefined}
                        onChange={(value) => setValue('advertType', value)}
                    />

                    <RenderIf condition={watch('propertyType') !== String(PROPERTY_TYPE.LAND)}>
                        <CustomSelectPrimary
                            options={roomCountOptions}
                            placeholder={t('advert.room-count')}
                            value={watch('roomCount') || undefined}
                            onChange={(value) => setValue('roomCount', value)}
                        />
                    </RenderIf>
                    <CustomSelectPrimary
                        loading={isCountriesLoading}
                        options={propertyTypeOptions}
                        value={watch('propertyType') || undefined}
                        placeholder={t('advert.property-type')}
                        onChange={(value) => setValue('propertyType', value)}
                    />
                    <HideOn device="mobile">
                        <CustomSelectPrimary
                            options={countryOptions}
                            value={watch('countryId')}
                            placeholder={t('general.country')}
                            onChange={(value) => {
                                setValue('countryId', value);
                                setValue('cityId', '');
                            }}
                        />
                    </HideOn>
                    <HideOn device="mobile">
                        <CustomSelectPrimary
                            disabled={!countryIdValue}
                            loading={isCitiesLoading}
                            options={cityOptions}
                            value={watch('cityId')}
                            placeholder={t('general.city')}
                            onChange={(value) => setValue('cityId', value)}
                        />
                    </HideOn>
                    <ButtonOutline
                        type="button"
                        text={t('filter.advanced-search')}
                        onClick={() => onOpen('advanced-filter')}
                        className="py-0.5 text-sm text-white md:text-brend md:border-brend border-white outline-none"
                    />
                    <ButtonPrimary type="submit" text={t('filter.search')} />
                    <ButtonPrimary
                        type="button"
                        disabled={!hasAnyFilter}
                        icon={<FilterXIcon />}
                        onClick={onReset}
                        className="max-w-[50px]"
                    />
                </div>
            </form>
        </div>
    );
};

export default ListingFilters;
